import dispatcher from "../dispatcher"
import * as api from "../api"
import AccountStore from "../stores/AccountStore"
import AppStateStore from "../stores/AppStateStore"
import * as utils from "../helpers/utils"
import { EPlanType, IHierarchyItem, IPrivacyAgreementDocument, IScheduleTemplate } from "../models"

function RestoreAccount(cb = null) {
	api.getAccount(AccountStore.getCurrentAccount().Id).then(res => {
		dispatcher.dispatch({
			type: "UPDATE_ACCOUNT",
			account: res.data
		})
		AppStateStore.getProgressSnackbarContent() &&
			AppStateStore.setProgressSnackbarContent("")
		if (cb && typeof cb === "function") cb()
	})
}

export function getAccount(accountId, cb = null) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	return api
		.getAccount(accountId)
		.then((response) => {
			dispatcher.dispatch({ type: "UPDATE_ACCOUNT", account: response.data })
			if (cb && typeof cb === "function") {
				cb()
			}
			return response
		})
		.then(() => {
			if (AccountStore.Timezones.length === 0) AccountStore.init()
		})
}

export function patchAccount(accountId, account, cb = null) {
	!AppStateStore.getProgressSnackbarContent() &&
		AppStateStore.setProgressSnackbarContent("loading")
	return api.patchAccount(accountId, account)
		.then(() => RestoreAccount(cb))
		.catch(() => cb && typeof cb === "function" && cb("fail"))
}

export function patchSpecificAccount(accountId, account, accountTicks, cb = null) {
	AppStateStore.setProgressSnackbarContent("loading")
	return api.accountManagerPatchSpecificAccount(accountId, account, accountTicks)
		.then((res) => {
			AppStateStore.setProgressSnackbarContent("")
			if (cb && typeof cb === "function") cb(res)
		})
		.catch(() => cb && typeof cb === "function" && cb("fail"))
}

export function patchAccountBillingContact(accountId, account, cb = null) {
	!AppStateStore.getProgressSnackbarContent() &&
		AppStateStore.setProgressSnackbarContent("loading")
	return api.patchAccountBillingContact(accountId, account)
		.then(() => RestoreAccount(cb))
		.catch(() => cb && typeof cb === "function" && cb("fail"))
}

export function updateAccountPlan(accountId: string, planType: EPlanType, enableSchedule: boolean, enableTimeoff: boolean, isAccountManager = false) {
	!AppStateStore.getProgressSnackbarContent() &&
		AppStateStore.setProgressSnackbarContent("loading")

	return api.updateAccountPlanType(accountId, planType, enableSchedule, enableTimeoff)
		.then(() => !isAccountManager ? RestoreAccount() : AppStateStore.setProgressSnackbarContent(""))
		.catch(() => { })
}

export function updateScheduleFeature(accountId, featureOn, isAccountManager = false, cb = null) {
	!AppStateStore.getProgressSnackbarContent() &&
		AppStateStore.setProgressSnackbarContent("loading")

	return api.updateAccountScheduleFeature(accountId, featureOn)
		.then(() => !isAccountManager ? RestoreAccount(cb) : AppStateStore.setProgressSnackbarContent(""))
		.catch(() => cb && typeof cb === "function" && cb("fail"))
}

export function updateTimeoffFeature(accountId, featureOn, isAccountManager = false, cb = null) {
	!AppStateStore.getProgressSnackbarContent() &&
		AppStateStore.setProgressSnackbarContent("loading")

	return api.updateAccountTimeoffFeature(accountId, featureOn)
		.then(() => !isAccountManager ? RestoreAccount(cb) : AppStateStore.setProgressSnackbarContent(""))
		.catch(() => cb && typeof cb === "function" && cb("fail"))
}

export function patchAccountHierarchySettings(accountId, accountHierarchySettings, asyncSessionIdForPaySetting = null) {
	!AppStateStore.getProgressSnackbarContent() &&
		AppStateStore.setProgressSnackbarContent("loading")
	asyncSessionIdForPaySetting &&
		setTimeout(() => dispatcher.dispatch({ type: "UPDATE_ASYNC_SESSION_ID", data: asyncSessionIdForPaySetting }), 0)
	api.patchAccountHierarchySettings(accountId, accountHierarchySettings, asyncSessionIdForPaySetting ? asyncSessionIdForPaySetting : "")
		.then(() => {
			asyncSessionIdForPaySetting &&
				clearAsyncSessionId()
			RestoreAccount()
		})
		.catch(() => {

		})
}

export function updatePayrollDefinitionLevel(accountId, payrollDefinitionLevelId, cb = null) {
	const asyncSessionId = utils.guid()
	setTimeout(() => dispatcher.dispatch({ type: "UPDATE_ASYNC_SESSION_ID", data: asyncSessionId }), 0)

	api.updatePayrollDefinitionLevel(accountId, payrollDefinitionLevelId, asyncSessionId)
		.then(() => {
			clearAsyncSessionId()
			RestoreAccount(cb)
		})
		.catch(() => { })
}

export function updateAccountHierarchyDefinitions(accountId, hierarchyDefinitionsList) {
	api.updateAccountHierarchyDefinitions(accountId, hierarchyDefinitionsList)
		.then(() => RestoreAccount())
		.catch(() => { })
}

export function addHierarchyItem(hierarchyItem: IHierarchyItem, cb = null) {
	api.addHierarchyItem(AccountStore.getCurrentAccount().Id, hierarchyItem)
		.then(() => RestoreAccount(cb))
		.catch(err => AppStateStore.setProgressSnackbarContent(err.response.data.message))
}

export function updateAccountHierarchyItem(accountId, hierarchyItem, needSpinner: boolean, asyncSessionIdForPaySetting = null, cb = null) {
	needSpinner && !AppStateStore.getProgressSnackbarContent() &&
		AppStateStore.setProgressSnackbarContent("loading")
	asyncSessionIdForPaySetting &&
		setTimeout(() => dispatcher.dispatch({ type: "UPDATE_ASYNC_SESSION_ID", data: asyncSessionIdForPaySetting }), 0)
	api.updateAccountHierarchyItem(accountId, hierarchyItem, asyncSessionIdForPaySetting ? asyncSessionIdForPaySetting : "")
		.then(() => {
			clearAsyncSessionId()
			RestoreAccount(cb)
		})
		.catch(() => { })
}

export function getAccountUsers(accountId) {
	return api.getAccountUsers(accountId).then((response) => {
		dispatcher.dispatch({
			type: "UPDATE_ACCOUNT_USERS",
			accountUsers: response.data.Documents
		})
	})
}

export function addAccountUser(accountId, user) {
	return api.addAccountUser(accountId, user)
		.then(res => {
			dispatcher.dispatch({
				type: "UPDATE_ACCOUNT_USERS",
				accountUsers: res.data.Documents
			})
			getAccountUsers(accountId)
		})
		.catch(() => { })
}

export function addPositionDefinition(accountId, position, cb = null) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.addPositionDefinition(accountId, position)
		.then(() => RestoreAccount(cb))
		.catch(() => { })
}

export function updatePositionDefinition(accountId, position, cb = null) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.updatePositionDefinition(accountId, position)
		.then(() => RestoreAccount(cb))
		.catch(() => { })
}

export function deletePositions(accountId, positions, cb) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.deletePositions(accountId, positions.map(item => item.Id))
		.then(() => RestoreAccount(cb))
		.catch(() => { })
}

export function addHierarchyPosition(hierarchyItemId, position, cb = null) {
	api.addHierarchyPosition(hierarchyItemId, position)
		.then(() => RestoreAccount(cb))
		.catch(() => { })
}

export function updateHierarchyPosition(hierarchyItemId, position, cb = null) {
	api.updateHierarchyPosition(hierarchyItemId, position)
		.then(() => RestoreAccount(cb))
		.catch(() => { })
}

export function removeHierarchyPositions(hierarchyId, positionIds, cb) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.removeHierarchyPositions(hierarchyId, positionIds)
		.then(() => RestoreAccount(cb))
		.catch(() => { })
}

export function deleteHierarchyItem(accountId, hierarchyIds, cb = null) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.deleteHierarchyItems(accountId, hierarchyIds)
		.then(() => RestoreAccount(cb))
		.catch(() => { })
}

export function addPayType(accountId, payType, cb) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.addPayType(accountId, payType)
		.then(() => RestoreAccount(cb))
		.catch(() => { })
}

export function updatePayType(accountId, payType, cb) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.updatePayType(accountId, payType)
		.then(() => RestoreAccount(cb))
		.catch(() => { })
}

export function deletePayType(accountId, payTypeId, cb) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.deletePayType(accountId, payTypeId)
		.then(() => RestoreAccount(cb))
		.catch(() => { })
}

export function addOTPolicy(accountId, otPolicy, cb) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.addOTPolicy(accountId, otPolicy)
		.then(() => RestoreAccount(cb))
		.catch(() => { })
}

export function updateOTPolicy(accountId, otPolicy, cb) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.updateOTPolicy(accountId, otPolicy)
		.then(() => RestoreAccount(cb))
		.catch(() => { })
}

export function deleteOTPolicy(accountId, otPolicyId, cb) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.deleteOTPolicy(accountId, otPolicyId)
		.then(() => RestoreAccount(cb))
		.catch(err => AppStateStore.setProgressSnackbarContent(err.response.data.message))
}

export function addPayGroup(accountId, payGroup, cb) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.addPayGroup(accountId, payGroup)
		.then(() => RestoreAccount(cb))
		.catch(() => { })
}

export function updatePayGroup(accountId, payGroup, cb) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })

	const asyncSessionId = utils.guid()
	setTimeout(() => dispatcher.dispatch({ type: "UPDATE_ASYNC_SESSION_ID", data: asyncSessionId }), 0)

	api.updatePayGroup(accountId, payGroup, asyncSessionId)
		.then(() => {
			clearAsyncSessionId()
			RestoreAccount(cb)
		})
		.catch(() => { })
}

export function deletePayGroup(accountId, payGroupId, cb) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.deletePayGroup(accountId, payGroupId)
		.then(() => RestoreAccount(cb))
		.catch(() => { })
}

export function addAccrual(accountId, accrual, cb) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.addAccrual(accountId, accrual)
		.then(() => RestoreAccount(cb))
		.catch(() => { })
}

export function updateAccrual(accountId, accrual, cb) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.updateAccrual(accountId, accrual)
		.then(() => RestoreAccount(cb))
		.catch(() => { })
}

export function deleteAccrual(accountId, accrualId, cb) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.deleteAccrual(accountId, accrualId)
		.then(res => {
			res.data === null ?
				cb(false) :
				RestoreAccount(cb)
		})
		.catch(() => { })
}

export function addHolidayTemplate(accountId, holidayTemplate, cb) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.addHolidayTemplate(accountId, holidayTemplate)
		.then(() => RestoreAccount(cb))
		.catch(() => { })
}

export function updateHolidayTemplate(accountId, holidayTemplate, cb) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })

	const asyncSessionId = utils.guid()
	setTimeout(() => dispatcher.dispatch({ type: "UPDATE_ASYNC_SESSION_ID", data: asyncSessionId }), 0)

	return api.updateHolidayTemplate(accountId, holidayTemplate, asyncSessionId)
		.then(() => {
			clearAsyncSessionId()
			this.getAccount(accountId, cb)
		})
		.catch(() => { })
}

export function deleteHolidayTemplate(accountId, holidayTemplateId, cb) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.deleteHolidayTemplate(accountId, holidayTemplateId)
		.then(() => RestoreAccount(cb))
		.catch(() => { })
}

export function getTimezonesList() {
	return api.getTimezoneList().then((response) => {
		return response
	})
}

export function addShiftTemplate(accountId, shiftTemplate, cb) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.addShiftTemplate(accountId, shiftTemplate)
		.then(() => RestoreAccount(cb))
		.catch(() => { })
}

export function updateShiftTemplate(accountId, shiftTemplate, cb) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	return api.updateShiftTemplate(accountId, shiftTemplate)
		.then(() => {
			RestoreAccount(cb)
		})
		.catch(() => { })
}

export function deleteShiftTemplates(accountId, shiftTemplateIds, cb) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.deleteShiftTemplates(accountId, shiftTemplateIds)
		.then(() => RestoreAccount(cb))
		.catch(() => { })
}

export function addPatternTemplate(accountId, patternTemplate, cb) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.addPatternTemplate(accountId, patternTemplate)
		.then(() => RestoreAccount(cb))
		.catch(() => { })
}

export function updatePatternTemplate(accountId, patternTemplate, cb?) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.updatePatternTemplate(accountId, patternTemplate)
		.then(() => RestoreAccount(cb))
		.catch(() => { })
}

export function deletePatternTemplates(accountId, patternTemplateIds, cb) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.deletePatternTemplates(accountId, patternTemplateIds)
		.then(() => RestoreAccount(cb))
		.catch(() => { })
}

export function addScheduleTemplate(accountId, scheduleTemplate: IScheduleTemplate, cb) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.addScheduleTemplate(accountId, scheduleTemplate)
		.then(() => RestoreAccount(cb))
		.catch(err => AppStateStore.setProgressSnackbarContent(err.response.data.message))
}

export function addScheduleTemplates(accountId, scheduleTemplates: IScheduleTemplate[], cb) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.addScheduleTemplates(accountId, scheduleTemplates)
		.then(() => RestoreAccount(cb))
		.catch(err => {
			AppStateStore.setProgressSnackbarContent(err.response.data.message)
		})
}

export function updateScheduleTemplate(accountId, scheduleTemplate, cb = null) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.updateScheduleTemplate(accountId, scheduleTemplate)
		.then(() => RestoreAccount(cb))
		.catch(() => { })
}

export function deleteScheduleTemplates(accountId, scheduleTemplateIds, cb) {
	dispatcher.dispatch({ type: "RETRIEVING_ACCOUNT" })
	api.deleteScheduleTemplates(accountId, scheduleTemplateIds)
		.then(() => RestoreAccount(cb))
		.catch(() => { })
}

export function clearAsyncSessionId() {
	setTimeout(() => dispatcher.dispatch({ type: "UPDATE_ASYNC_SESSION_ID", data: null }), 0)
}

export function createAgreement(agreement: IPrivacyAgreementDocument, cb = null) {
	api.addAccountAgreement(agreement)
		.then(() => {
			if (cb && typeof cb === "function") {
				cb()
			}
		})
		.catch(() => { })
}

